import { Trans, t } from '@lingui/macro';
import { X, ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { SoribotSimplified } from '../../models/Soribot';
import { Fire } from '../../services/FireClient';
import { My } from '../../services/My';
import { LocalStorage } from '../../services/Utils/LocalStorage';
import { SoribotChatContent } from '../rooms/right-menu/BotSoriPrivateModal';
import { useHistory } from '../../hooks/useRouterCompat';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { useToast } from "../../components/ui/use-toast";
import { cn } from "../../lib/utils";

const PublicSoribotPage: React.FC = () => {
  const [inProgress, setInProgress] = useState(false);
  const { toast } = useToast();
  const { publicId } = useParams<{ publicId: string; }>();
  const [bot, setBot] = useState<SoribotSimplified | null>(null);

  // Passcode Dialog State
  const [isPasscodeDialogOpen, setIsPasscodeDialogOpen] = useState(false);
  const [passcodeHeader, setPasscodeHeader] = useState("");
  const [inputPasscode, setInputPasscode] = useState("");

  const history = useHistory();

  useEffect(() => {
    load("");
  }, [publicId])

  async function load(code: string) {
    try {
      setInProgress(true);
      setIsPasscodeDialogOpen(false);

      const r = await Fire.bot.getSoribotByPublicId({ publicId: publicId || "", passcode: code });

      if (r.status === 'DISABLED') {
        toast({ variant: "destructive", description: t`This soribot is disabled` });
        setBot(null);
      } else if (r.status === 'PASSCODE_REQUIRED') {
        showPasscodePrompt(t`Passcode is required`);
      } else if (r.status === 'INCORRECT_PASSCODE') {
        showPasscodePrompt(t`Incorrect passcode`);
      } else if (!r.soribot) {
        toast({ variant: "destructive", description: t`Soribot not found` });
        setBot(null);
      } else {
        setBot(r.soribot);
      }

      setInProgress(false);
    } catch (error: any) {
      console.warn(error.message);
      toast({ variant: "destructive", description: error.message });
      setInProgress(false);
    }
  }

  function showPasscodePrompt(header: string) {
    const savedPasscode = LocalStorage.get(`${publicId}-passcode`) || "";
    setInputPasscode(savedPasscode);
    setPasscodeHeader(header);
    setIsPasscodeDialogOpen(true);
  }

  function handlePasscodeSubmit() {
    load(inputPasscode);
    LocalStorage.set(`${publicId}-passcode`, inputPasscode);
    setIsPasscodeDialogOpen(false);
  }

  return (
    <div className={cn("flex flex-col h-screen bg-muted/50", bot?.style ?? 'default-style')}>
      {/* Loading Bar */}
      {inProgress && <Progress value={undefined} className="absolute top-0 left-0 w-full z-50 h-1" />}

      {/* Main Content */}
      {bot && (
        <div className="flex flex-col h-full">
          <header className="flex items-center justify-between px-4 h-14 border-b bg-background shrink-0">
            <div className="flex-1 min-w-0 flex justify-center">
              <h1 className="text-lg font-semibold truncate">{bot?.name}</h1>
            </div>

            <div className="flex items-center absolute right-4">
              {My.isLoggedIn() && (
                <Button variant="ghost" size="icon" onClick={() => history.push('/')}>
                  <X className="w-5 h-5 text-muted-foreground" />
                </Button>
              )}
              {!My.isLoggedIn() && (
                <a className='text-xs text-muted-foreground hover:text-primary transition-colors' href="https://sorichat.com" target='_blank'>
                  <Trans>Powered by NEXTPOD</Trans>
                </a>
              )}
            </div>
          </header>

          <div className="flex-1 overflow-hidden relative">
            <SoribotChatContent bot={bot} room={null} />
          </div>
        </div>
      )}

      {/* Passcode Dialog */}
      <Dialog open={isPasscodeDialogOpen} onOpenChange={setIsPasscodeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{passcodeHeader}</DialogTitle>
            <DialogDescription><Trans>Please enter the passcode to access this bot.</Trans></DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Input
              type="password"
              placeholder={t`Enter up to 6 digits`}
              maxLength={6}
              value={inputPasscode}
              onChange={(e) => setInputPasscode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handlePasscodeSubmit()}
            />
          </div>
          <DialogFooter>
            <Button onClick={handlePasscodeSubmit}><Trans>OK</Trans></Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicSoribotPage;
